import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import ImagePreview from "./ImagePreview/ImagePreview";
import VideoPreview from "./VideoPreview/VideoPreview";
import AudioPreview from "./AudioPreview/AudioPreview";
import FilePreview from "./FilePreview/FilePreview";
import AudioDisplay from "./Display/AudioDisplay";
import ImageDisplay from "./Display/ImageDisplay";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";
import VideoDisplay from "./Display/VideoDisplay";
import { ReactComponent as DeleteIcon } from "assets/img/icons/Delete.svg";
import { useGlobalContext } from "store/contexts/globalContext";

const styles = {
  previewDialog: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "fixed",
    top: "0",
    left: "0",
    zIndex: "1100",
    color: "black",
  },
  previewDialogBackdrop: {
    height: "inherit",
    width: "inherit",
    position: "fixed",
    backgroundColor: "black",
    top: "0",
    left: "0",
    zIndex: "-1",
    pointerEvents: "none",
    opacity: "0.7",
  },
  closeIcon: {
    width: "25px",
    cursor: "pointer",
    fill: "white",
  },

  closeContainer: {
    padding: "20px",
    position: "absolute",
    top: 0,
    right: 0,
  },
};

export default ({
  removePreviewAttachment,
  imageStyle,
  attachment,
  clearAttachment,
  commentAttachmentPreview = false,
  commentDocumentPreview = false,
  showPreview = false,
  setShowPreview = null,
}) => {
  const classes = styles;
  const [attachmentSrc, setAttachmentSrc] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const { microsoftConfigureTab } = useGlobalContext();
  const prevUrlRef = useRef(null);

  // Determine attachment type based on the file type
  const determineAttachmentType = () => {
    // Handle null or undefined attachment
    if (!attachment) {
      return "document";
    }

    // For File objects
    if (attachment instanceof File) {
      const fileType = attachment.type.toLowerCase();
      // Get by file extension if MIME type is not reliable
      const fileName = attachment.name.toLowerCase();
      
      if (fileType.includes("image") || /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(fileName)) {
        return "image";
      }
      if (fileType.includes("video") || /\.(mp4|webm|ogg|mov|avi|wmv|mkv)$/i.test(fileName)) {
        return "video";
      }
      if (fileType.includes("audio") || /\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(fileName)) {
        return "audio";
      }
      return "document";
    }
    
    // For API attachments with mimeType property
    if (attachment.mimeType) {
      const mimeType = attachment.mimeType.toLowerCase();
      if (mimeType.includes("image")) return "image";
      if (mimeType.includes("video")) return "video";
      if (mimeType.includes("audio")) return "audio";
      return "document";
    }
    
    // For File objects or other attachment formats
    if (attachment.type) {
      const fileType = attachment.type.toLowerCase();
      if (fileType.includes("image")) return "image";
      if (fileType.includes("video")) return "video";
      if (fileType.includes("audio")) return "audio";
      return "document";
    }
    
    // For URL-based determination
    if (typeof attachment === 'string') {
      if (/\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(attachment)) return "image";
      if (/\.(mp4|webm|ogg|mov|avi|wmv|mkv)$/i.test(attachment)) return "video";
      if (/\.(mp3|wav|ogg|m4a|aac|flac)$/i.test(attachment)) return "audio";
      return "document";
    }
    
    // Default to document type if unable to determine
    return "document";
  };

  const type = determineAttachmentType();

  useEffect(() => {
    if (!showPreview) {
      return;
    }

    setShowPreviewDialog(showPreview);
  }, [showPreview]);

  useEffect(() => {
    if (!attachment) {
      return;
    }
    
    if (prevUrlRef.current && prevUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(prevUrlRef.current);
    }
    
    let newUrl = null;
    
    // Handle both File objects and API attachment objects with URLs
    if (attachment.url) {
      // For API attachments that have direct URLs
      newUrl = attachment.url;
    } else if (attachment.preview) {
      // For objects with preview property
      newUrl = attachment.preview;
    } else if (attachment instanceof File || attachment instanceof Blob) {
      // For File or Blob objects (newly uploaded)
      newUrl = URL.createObjectURL(attachment);
    } else if (typeof attachment === 'string') {
      // For direct URL strings
      newUrl = attachment;
    }
    
    setAttachmentSrc(newUrl);
    prevUrlRef.current = newUrl;
    
    return () => {
      if (newUrl && newUrl.startsWith('blob:')) {
        URL.revokeObjectURL(newUrl);
      }
    };
  }, [attachment]);

  const preventDefaultOnClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
  };

  const showPreviewDailog = () => {
    try {
      if (!microsoftConfigureTab && document.getElementById("footer")) {
        document.getElementById("footer").style.zIndex = "-1";
      }

      if (document.getElementById("right-sidebar")) {
        document.getElementById("right-sidebar").style.zIndex = "-1";
      }
      
      if (document.getElementById("header")) {
        document.getElementById("header").style.zIndex = "-1";
      }
      
      setShowPreviewDialog(true);
    } catch (e) {
      console.error("Error showing preview dialog:", e);
      setShowPreviewDialog(true);
    }
  };

  const closePreviewDailog = () => {
    try {
      if (!microsoftConfigureTab && document.getElementById("footer")) {
        document.getElementById("footer").style.zIndex = "1";
      }

      if (document.getElementById("right-sidebar")) {
        document.getElementById("right-sidebar").style.zIndex = "1";
      }
      
      if (document.getElementById("header")) {
        document.getElementById("header").style.zIndex = "1";
      }
      
      setShowPreviewDialog(false);
      if (setShowPreview) {
        setShowPreview(false);
      }
    } catch (e) {
      console.error("Error closing preview dialog:", e);
      setShowPreviewDialog(false);
      if (setShowPreview) {
        setShowPreview(false);
      }
    }
  };

  if (!attachment) return null;
  return (
    <>
      <div
        onClick={showPreviewDailog}
        className={classNames({
          [commentAttachmentPreview]: commentAttachmentPreview ? true : false,
          attachmentPreview: !commentAttachmentPreview ? true : false,
          [commentDocumentPreview]:
            commentDocumentPreview && type === "document" ? true : false,
        })}
        style={type === "document" ? { pointerEvents: "none" } : {}}
      >
        {type === "image" && (
          <ImagePreview
            attachmentSrc={attachmentSrc}
            attachmentStyle={imageStyle}
          />
        )}
        {type === "video" && <VideoPreview attachmentSrc={attachmentSrc} />}
        {type === "audio" && <AudioPreview attachmentSrc={attachmentSrc} />}
        {type === "document" && <FilePreview documentAttachment={attachment} />}
        <DeleteIcon
          onClick={clearAttachment}
          className={classNames({
            removeAttachment: removePreviewAttachment ? false : true,
            [removePreviewAttachment]: removePreviewAttachment ? true : false,
          })}
        />
      </div>
      {showPreviewDialog && (
        <div style={classes.previewDialog} onClick={closePreviewDailog}>
          <div style={classes.previewDialogBackdrop}></div>
          <div style={classes.closeContainer}>
            <CloseIcon style={classes.closeIcon} onClick={closePreviewDailog} />
          </div>
          <div
            className={classNames({
              imagePreviewContentContainer: type === "image" ? true : false,
              previewContentContainer: type !== "image" ? true : false,
            })}
            style={
              type === "document"
                ? {
                    maxWidth: "unset",
                  }
                : {}
            }
            onClick={preventDefaultOnClick}
          >
            {type === "image" && (
              <ImageDisplay
                attachmentSrc={attachmentSrc}
                type={attachment.type || attachment.mimeType || "image"}
              />
            )}
            {type === "audio" && (
              <AudioDisplay
                attachmentSrc={attachmentSrc}
                type={attachment.type || attachment.mimeType || "audio"}
              />
            )}
            {type === "video" && (
              <VideoDisplay
                attachmentSrc={attachmentSrc}
                type={attachment.type || attachment.mimeType || "video"}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
